import React, { useCallback, useEffect, useState } from 'react';
import { Dropdown, DropdownProps } from 'semantic-ui-react';
import { changeHost, endParty } from '../../api/user';
import { Socket } from 'socket.io-client';

type IHostLeaveDialog = {
  open: boolean;
  setOpen: () => void;
  roomId: string;
  socket: Socket;
  participants: User[];
  nameMap: StringDict;
  pictureMap: StringDict;
  setMedia: (e: any, data: DropdownProps) => void;
  redirectToWatchPartyDomain: () => void;
  handleClickOnNo: () => void;
  owner: string | undefined;
};

const HostLeaveDialog: React.FC<IHostLeaveDialog> = ({
  open,
  setOpen,
  roomId,
  participants,
  socket,
  nameMap,
  pictureMap,
  setMedia,
  redirectToWatchPartyDomain,
  handleClickOnNo,
  owner,
}) => {
  const [options, setOptions] = useState<
    { key: string; text: string; value: string; image: string }[]
  >([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [showMakeHost, setShowMakeHost] = useState<boolean>(false);

  const handleWatchPartyEnd = useCallback(
    async (roomId: string) => {
      try {
        await endParty(roomId);
        setMedia(null, { value: '' });
        socket.emit('CMD:endParty');
        handleClickOnNo();
      } catch (e) {
        console.log(e);
      }
    },
    [socket, setMedia, handleClickOnNo]
  );

  const handleChangeHost = useCallback(
    async (roomId: string, userId: string) => {
      try {
        const res = await changeHost(userId, roomId);
        socket.emit('CMD:setRoomCoHost', {
          uid: userId,
          token: res.data.token,
        });
        // socket.emit('CMD:setRoomOwner', { uid: userId, token: res.data.token });
        setOpen();
        redirectToWatchPartyDomain();
      } catch (e) {
        console.log(e);
      }
    },
    [setOpen, redirectToWatchPartyDomain, socket]
  );

  useEffect(() => {
    const options: {
      key: string;
      text: string;
      value: string;
      image: string;
    }[] = [];
    if (
      participants.length &&
      Object.keys(nameMap).length > 0 &&
      Object.keys(pictureMap).length > 0
    ) {
      for (let i = 0; i < participants.length; i++) {
        options.push({
          key: participants[i].uid ?? '',
          image: pictureMap[participants[i].id as string],
          text: nameMap[participants[i].id],
          value: participants[i].uid ?? '',
        });
      }
      setOptions(options);
    }
  }, [participants, nameMap, pictureMap]);

  useEffect(() => {
    if (!open) {
      setShowMakeHost(false);
    }
  }, [open, setOpen]);

  return (
    <div
      className={`fixed inset-0 size-full z-[9999] bg-black/50 grid place-items-center ${
        open ? 'block' : 'hidden'
      }`}
    >
      <div className="gradient-modal overflow-hidden rounded-lg max-w-lg w-full relative">
        <div>
          {!showMakeHost ? (
            <div>
              <div className="flex items-center justify-between p-3 bg-[#3d026c]">
                <h5 className="text-gradient-blue text-2xl">Assign co host</h5>
                <button
                  onClick={() => {
                    setShowMakeHost(false);
                    setOpen();
                  }}
                  className="text-gradient-pink text-3xl font-bold"
                >
                  <i className="ri-close-fill"></i>
                </button>
              </div>
              <p className="text-[#ae8a27] text-[20px] p-3 text-start !my-5 block">
                Would you like to assign co host?
              </p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between p-3 bg-[#3d026c]">
                <h5 className="text-gradient-blue text-2xl">Select co host</h5>
                <button
                  onClick={() => {
                    setShowMakeHost(false);
                    setOpen();
                  }}
                  className="text-gradient-pink text-3xl font-bold"
                >
                  <i className="ri-close-fill"></i>
                </button>
              </div>
              <div className="p-4">
                <Dropdown
                  placeholder="Select Friend"
                  fluid
                  selection
                  options={options.filter((i) => i.key !== owner)}
                  value={selectedUser}
                  onChange={(e, data) => {
                    setSelectedUser(data.value as unknown as string);
                  }}
                  className="gradient-outline-btn text-white !p-2"
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-end gap-3 p-4 border-t border-t-white/10">
            {!showMakeHost ? (
              <>
                <button
                  className="btn-gradient-red py-3 px-6 text-[16px] font-semibold rounded-full text-white duration-150 hover:opacity-70"
                  onClick={() => handleWatchPartyEnd(roomId)}
                >
                  No
                </button>
                <button
                  onClick={() => setShowMakeHost(true)}
                  className="gradient-outline-btn py-3 px-6 text-[16px] font-semibold rounded-full duration-150 hover:opacity-70"
                >
                  <span>Select host</span>
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn-gradient-red py-3 px-6 text-[16px] font-semibold rounded-full text-white duration-150 hover:opacity-70"
                  onClick={() => setShowMakeHost(false)}
                >
                  <span>Back</span>
                </button>
                <button
                  disabled={!selectedUser}
                  onClick={() => handleChangeHost(roomId, selectedUser)}
                  className="gradient-outline-btn py-3 px-6 text-[16px] font-semibold rounded-full duration-150 hover:opacity-70"
                >
                  <span>Make host</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostLeaveDialog;
